import { useEffect } from 'react';
import { create } from 'zustand';
import { AdvanceQuestionType } from 'views/templates/accountOpen/advanceQuestion/hooks';
import { ContactInfoWrapper } from 'views/templates/accountOpen/contactInfo';
import { DealerInfoWrapper } from 'views/templates/accountOpen/dealerInfo';
import { EntryComplete } from 'views/templates/accountOpen/entryComplete';
import { EntryConfirm } from 'views/templates/accountOpen/entryConfirm';
import { RepresentInfoWrapper } from 'views/templates/accountOpen/representInfo';
import { RulerInfoWrapper } from 'views/templates/accountOpen/rulerInfo';
import { TransactionPurposeWrapper } from 'views/templates/accountOpen/transactionPurpose';
import { CorporateInfoWrapper } from 'views/templates/accountOpen/corporateInfo';
import { InvestorCategoryInfoWrapper } from 'views/templates/accountOpen/investorCategoryInfo';
import { SelectGeneralInvestorWrapper } from 'views/templates/accountOpen/selectGeneralInvestor';
import { OfferConfirm } from 'views/templates/accountOpen/offerConfirm';
import { ConsentConfirm } from 'views/templates/accountOpen/consentConfirm';
import { SpecialInvestorConfirm } from 'views/templates/accountOpen/specialInvestorConfirm';
import { TFAuthenticationWrapper } from 'views/templates/accountOpen/tfAuthentication';
import { useCorporateAccountEstablishStore } from 'views/templates/accountOpen/hooks';
import { ErrorPage } from 'views/pages/errorPage';
import { PagePath } from 'constants/pagePath';
import { useGetAdvanceQuestionQuery } from 'views/utils/graphql/graphql';

export enum CorporateAccountOpenProfileDetailsPages {
  corporateInfo,
  transactionPurpose,
  contactInfo,
  representInfo,
  dealerInfo,
  rulerInfo,
  IntroducerInfo,
  entryConfirm,
  entryComplete,
  investorCategoryInfo,
  selectGeneralInvestor,
  offerConfirm,
  consentConfirm,
  specialInvestorConfirm,
  tfAuthentication,
  errorPage,
}

export const useCorporateAccountOpenProfileDetails = () => {
  const { accountOpenProfileDetailsPageType, reset } = useCorporateAccountOpenProfileDetailsPageTypeStore();

  useEffect(() => {
    return () => {
      reset();
    };
  }, []);

  const SwitchPage = (page: CorporateAccountOpenProfileDetailsPages) => {
    switch (page) {
      // 法人情報
      case CorporateAccountOpenProfileDetailsPages.corporateInfo:
        return <CorporateInfoWrapper />;

      // 取引目的
      case CorporateAccountOpenProfileDetailsPages.transactionPurpose:
        return <TransactionPurposeWrapper />;

      // 連絡先情報
      case CorporateAccountOpenProfileDetailsPages.contactInfo:
        return <ContactInfoWrapper />;

      // 代表者情報
      case CorporateAccountOpenProfileDetailsPages.representInfo:
        return <RepresentInfoWrapper />;

      // 取引担当者情報
      case CorporateAccountOpenProfileDetailsPages.dealerInfo:
        return <DealerInfoWrapper />;

      // 実質的支配者のご申告
      case CorporateAccountOpenProfileDetailsPages.rulerInfo:
        return <RulerInfoWrapper />;

      case CorporateAccountOpenProfileDetailsPages.investorCategoryInfo:
        return <InvestorCategoryInfoWrapper />;

      case CorporateAccountOpenProfileDetailsPages.selectGeneralInvestor:
        return <SelectGeneralInvestorWrapper />;

      case CorporateAccountOpenProfileDetailsPages.offerConfirm:
        return <OfferConfirm />;
      case CorporateAccountOpenProfileDetailsPages.consentConfirm:
        return <ConsentConfirm />;

      case CorporateAccountOpenProfileDetailsPages.specialInvestorConfirm:
        return <SpecialInvestorConfirm />;
      //投資家区分情報
      //一般投資家への移行1
      //一般投資家への移行2
      //一般投資家への移行3
      //一般投資家への移行4

      case CorporateAccountOpenProfileDetailsPages.tfAuthentication:
        return <TFAuthenticationWrapper />;

      // 申込確認
      case CorporateAccountOpenProfileDetailsPages.entryConfirm:
        return <EntryConfirm />;

      // 申込完了
      case CorporateAccountOpenProfileDetailsPages.entryComplete:
        return <EntryComplete />;

      case CorporateAccountOpenProfileDetailsPages.errorPage:
        return (
          <ErrorPage
            message={
              <>
                有効期限が切れているかURLに誤りがあります。
                再度、口座開設お申込の手続きを実施いただくようお願いいたします。
              </>
            }
            linkText="口座開設お手続きに進む"
            linkPath={PagePath.AccountOpen}
          />
        );
    }
  };

  return {
    accountOpenProfileDetailsPageType,
    SwitchPage,
  };
};

//画面遷移管理
type AccountOpenProfileDetailsPageTypeStore = {
  accountOpenProfileDetailsPageType: CorporateAccountOpenProfileDetailsPages;
  previousPage: CorporateAccountOpenProfileDetailsPages;
  setAccountOpenProfileDetailsPageType: (navType: CorporateAccountOpenProfileDetailsPages) => void;
  setPreviousPage: (navType: CorporateAccountOpenProfileDetailsPages) => void;
  reset: () => void;
};

export const useCorporateAccountOpenProfileDetailsPageTypeStore = create<AccountOpenProfileDetailsPageTypeStore>(
  (set) => ({
    accountOpenProfileDetailsPageType: CorporateAccountOpenProfileDetailsPages.corporateInfo,
    previousPage: CorporateAccountOpenProfileDetailsPages.corporateInfo,
    setAccountOpenProfileDetailsPageType: (navType: CorporateAccountOpenProfileDetailsPages) =>
      set((state) => ({ accountOpenProfileDetailsPageType: navType })),
    setPreviousPage: (navType: CorporateAccountOpenProfileDetailsPages) => set((state) => ({ previousPage: navType })),
    reset: () => set({ accountOpenProfileDetailsPageType: CorporateAccountOpenProfileDetailsPages.corporateInfo }),
  }),
);

export const useAdvanceQuestionValueModule = (uniqueId: string | undefined) => {
  const { param, add, clear } = useCorporateAccountEstablishStore();
  const { setAccountOpenProfileDetailsPageType } = useCorporateAccountOpenProfileDetailsPageTypeStore();
  const [advanceQuestion, _] = useGetAdvanceQuestionQuery({
    variables: { id: uniqueId ?? '' },
    requestPolicy: 'network-only',
  });

  useEffect(() => {
    if (advanceQuestion.fetching) {
      return;
    } else if (
      advanceQuestion.data?.GetAdvanceQuestion == null ||
      advanceQuestion.data?.GetAdvanceQuestion?.AdvanceNationality != null
    ) {
      setAccountOpenProfileDetailsPageType(CorporateAccountOpenProfileDetailsPages.errorPage);
    } else {
      clear();
      const info: AdvanceQuestionType = {
        uniqueId: uniqueId ?? '',
        dealerQuestion: advanceQuestion.data?.GetAdvanceQuestion?.RepresentAndDealerAreSame ?? null,
        mailAddressQuestion: advanceQuestion.data?.GetAdvanceQuestion?.DealerMailAddress,
        identificationQuestion: advanceQuestion.data?.GetAdvanceQuestion?.HaveEKYCDocument,
        selfieQuestion: advanceQuestion.data?.GetAdvanceQuestion?.CanTakePicture,
        personalInformation: true,
        generalTermsCondition: true,
        confirmResponsibility: true,
      };
      add({
        ...param,
        ...info,
      });

      setAccountOpenProfileDetailsPageType(CorporateAccountOpenProfileDetailsPages.corporateInfo);
    }
  }, [advanceQuestion]);

  return {
    fetching: advanceQuestion.fetching,
  };
};
